// THI Enhanced Recovery After Cardiac Surgery (ERACS). Shared by the Enhanced
// Recovery tool (full pathway, by procedure type) and the Case Setup Guide
// (just what to have prepared). Items can be limited to procedure types with
// `appliesTo`; everything else applies to all.

export const ERACS_TYPES = [
  { id: 'sternotomy', label: 'Sternotomy' },
  { id: 'robotic', label: 'Robotic' },
  { id: 'thoracic', label: 'Thoracic' },
];

// Procedure types whose pathway content exists today; the rest are placeholders
export const ERACS_READY = ['sternotomy', 'robotic'];

export const eracsPathway = [
  {
    name: 'Multimodal analgesia',
    sections: [
      {
        main: 'Medications',
        items: [
          'Dexmedetomidine 0.2–0.4 mcg/kg/hr infusion',
          'Ketamine 50–100 mg intraop, or 0.2–0.4 mg/kg/hr infusion',
        ],
      },
      {
        main: 'Regional anesthesia',
        items: [
          { text: 'Single-shot parasternal blocks (SPIP)', appliesTo: ['sternotomy'] },
          { text: 'ESP or serratus anterior catheter', appliesTo: ['robotic'] },
        ],
      },
      {
        main: 'Ropivacaine dosing',
        items: [
          { text: '0.5% vial, dilute to 0.25% for single-shot blocks (20–30 mL per side)', appliesTo: ['sternotomy'] },
          { text: 'CADD cassette 0.2%: PIB 20 mL q2h ± PCRA 5 mL q1h PRN, or continuous 10 mL/hr + PCRA 5 mL q1h PRN', appliesTo: ['robotic'] },
        ],
      },
    ],
  },
  {
    name: 'Intraoperative management',
    sections: [
      { main: 'Antibiotic prophylaxis', items: ['Within 30–60 min of incision, ≤48 h duration'] },
      { main: 'Glycemic control', items: ['Maintain glucose <180 mg/dL (insulin infusion)'] },
      {
        main: 'Temperature',
        items: [
          'Normothermia 36–37 °C',
          'After separation from CPB, warm aggressively: raise OR temperature, forced-air warmer, reflective warming bouffant (by CVOR 5), fluid warmers',
        ],
      },
      { main: 'Goal-directed fluid therapy', items: ['Avoid fluid overload to protect pulmonary function'] },
    ],
  },
  {
    name: 'Bleeding management',
    sections: [
      { main: 'Antifibrinolytics', items: ['TXA or Amicar for all CPB cases'] },
      { main: 'ROTEM', items: ['Guides transfusion decisions'] },
    ],
  },
  {
    name: 'Facilitate early extubation',
    sections: [
      { main: 'Minimal paralytics', items: ['After CPB, to allow early extubation'] },
      { main: 'Minimize benzodiazepines', items: ['Less sedation, lower delirium risk'] },
      { main: 'Opioid-sparing strategy', items: ['Multimodal analgesia reduces opioid requirements'] },
      { main: 'Neuromuscular blockade reversal', items: ['Consider reversing before transport, or on arrival to ICU'] },
    ],
  },
];

/** The pathway with items filtered to one procedure type */
export const eracsPathwayFor = (typeId) =>
  eracsPathway
    .map((cat) => ({
      ...cat,
      sections: cat.sections
        .map((sec) => ({
          ...sec,
          items: sec.items
            .filter((it) => typeof it === 'string' || !it.appliesTo || it.appliesTo.includes(typeId))
            .map((it) => (typeof it === 'string' ? it : it.text)),
        }))
        .filter((sec) => sec.items.length > 0),
    }))
    .filter((cat) => cat.sections.length > 0);

// What to have drawn up or ready at case setup when the patient is on the pathway
export const eracsPrep = [
  'Dexmedetomidine infusion (0.2–0.4 mcg/kg/hr)',
  'Ketamine (50–100 mg bolus, or 0.2–0.4 mg/kg/hr infusion)',
  'Ropivacaine 0.5% for blocks (dilute to 0.25% for single-shot) or 0.2% CADD cassette for a catheter',
  'TXA or Amicar',
  'Insulin infusion available for glucose <180 mg/dL',
];
